/**
 * Apple Search Ads attribution → analytics payloads (platform-agnostic).
 *
 * The mobile app resolves Apple's AdServices attribution once per install and
 * hands the normalized record here. Two things are derived from it:
 *
 *  1. The `asa_attribution_resolved` EVENT payload (EVENTS.ASA_ATTRIBUTION_RESOLVED).
 *     Fired once per install at resolution time, under whatever distinct_id
 *     the device has. It carries the full record so campaign/keyword analysis
 *     works for users who never sign up. It must NOT depend on a person
 *     profile: PostHog's `identified_only` mode (the SDK default, and what the
 *     app runs) sends anonymous events personless, and `$set` / `$set_once`
 *     on a personless event are discarded server-side.
 *
 *  2. The first-touch PERSON payload (`$set_once`). Because of (1) it may
 *     only be stamped once the client is identified; before that it is
 *     deferred, not dropped, and never latched. Aug 2026: 1,680 of 2,616
 *     resolutions fired anonymously and were lost this way (PR #95).
 *
 * `acquisition_channel` values: 'apple_search_ads' | 'organic' | 'unknown'.
 * 'unknown' (simulator, or Apple never answered inside the retry window) is
 * carried on the event and super-props for debugging but is never stamped as
 * the canonical first touch, where it would be indistinguishable from a real
 * value and would lock the once-only latch.
 */

const ASA_RECORD_FIELDS = [
  'asa_org_id',
  'asa_campaign_id',
  'asa_ad_group_id',
  'asa_keyword_id',
  'asa_ad_id',
  'asa_conversion_type',
  'asa_country_or_region',
];

const orNull = (v) => (v === undefined || v === null || v === '' ? null : String(v));

/**
 * True when the record is a terminal resolution the app can act on.
 * @param {unknown} attribution
 */
export function isResolvedAsaAttribution(attribution) {
  return !!attribution && typeof attribution === 'object' && typeof attribution.acquisition_channel === 'string' && attribution.acquisition_channel !== '';
}

/**
 * Full payload for the `asa_attribution_resolved` event. Every ASA field is
 * present (null when not attributed) so the event schema is stable across
 * organic / paid / unknown and can be broken down without null-handling.
 *
 * `resolved_at` is the ms-epoch the resolver persisted the record. Records
 * stored by builds before this field existed have none; the event they fire
 * on first launch after upgrade is a BACKFILL of an old install, not a new
 * install, and is marked `backfilled: true` so install-volume dashboards can
 * exclude it.
 *
 * @param {object} attribution - normalized record from the AdServices resolver
 * @returns {object|null} event properties, or null when the record is not terminal
 */
export function buildAsaResolvedEventProps(attribution) {
  if (!isResolvedAsaAttribution(attribution)) return null;
  const resolvedAt = Number.isFinite(attribution.resolved_at) ? attribution.resolved_at : null;
  const props = {
    acquisition_channel: attribution.acquisition_channel,
    asa_attribution: attribution.acquisition_channel === 'apple_search_ads',
    resolved_at: resolvedAt,
    backfilled: resolvedAt === null,
  };
  for (const f of ASA_RECORD_FIELDS) props[f] = orNull(attribution[f]);
  return props;
}

/**
 * First-touch person properties (`$set_once`). Null when the channel is
 * 'unknown' or the record is not terminal: nothing canonical to stamp.
 *
 * @param {object} attribution
 * @returns {object|null}
 */
export function buildAsaFirstTouchProps(attribution) {
  if (!isResolvedAsaAttribution(attribution)) return null;
  if (attribution.acquisition_channel === 'unknown') return null;
  return {
    initial_acquisition_channel: attribution.acquisition_channel,
    initial_asa_campaign_id: orNull(attribution.asa_campaign_id),
    initial_asa_ad_group_id: orNull(attribution.asa_ad_group_id),
    initial_asa_keyword_id: orNull(attribution.asa_keyword_id),
  };
}

/**
 * What to do with the first-touch stamp right now.
 *
 *  - 'skip'  : already stamped, or nothing canonical to stamp ('unknown'/
 *              non-terminal). Caller does nothing.
 *  - 'defer' : there is a canonical record but the client is anonymous, so a
 *              `$set_once` would be discarded. Caller does nothing AND does not
 *              latch; the next call after identify() will return 'stamp'.
 *  - 'stamp' : identified and not yet stamped. Caller captures `$set_once`
 *              and latches.
 *
 * @param {{ attribution: object, isIdentified: boolean, alreadyStamped: boolean }} s
 * @returns {'skip'|'defer'|'stamp'}
 */
export function resolveAsaFirstTouchAction({ attribution, isIdentified, alreadyStamped }) {
  if (alreadyStamped) return 'skip';
  if (!buildAsaFirstTouchProps(attribution)) return 'skip';
  if (!isIdentified) return 'defer';
  return 'stamp';
}
